import { colors, spacing, typography } from '../design/tokens.ts'

export type Radius = 'none' | 'sm' | 'md' | 'lg'
export type FontPreset = 'system' | 'sans' | 'serif' | 'brand'
export type FontFamily = string

export interface Theme {
  colorActionPrimary?: string
  colorActionPrimaryHover?: string
  colorBackground?: string
  colorText?: string
  colorDanger?: string
  radius?: Radius
  fontFamily?: FontFamily
}

export type ThemeResult =
  { ok: true; theme: Theme } | { ok: false; reason: string }

type ColorKey = Exclude<keyof Theme, 'radius' | 'fontFamily'>

const COLOR_KEYS: readonly ColorKey[] = [
  'colorActionPrimary',
  'colorActionPrimaryHover',
  'colorBackground',
  'colorText',
  'colorDanger'
]

const COLOR_KEY_SET: ReadonlySet<string> = new Set(COLOR_KEYS)

const HEX = /^#[0-9A-Fa-f]{6}$/

// Each pair is a foreground that renders on colorBackground; buttons use it as their label colour.
const CONTRAST_PAIRS: readonly ColorKey[] = [
  'colorText',
  'colorActionPrimary',
  'colorActionPrimaryHover',
  'colorDanger'
]

const WCAG_AA_TEXT = 4.5

const CSS_VAR: Record<ColorKey, readonly string[]> = {
  colorActionPrimary: ['--color-action-primary'],
  colorActionPrimaryHover: [
    '--color-action-primary-hover',
    '--color-action-primary-active'
  ],
  colorBackground: [
    '--color-surface-raised',
    '--color-surface-page',
    '--color-status-danger-subtle',
    '--color-status-success-subtle',
    '--color-status-info-subtle',
    '--color-text-inverse'
  ],
  colorText: [
    '--color-text-body',
    '--color-text-heading',
    '--color-text-secondary',
    '--color-action-secondary'
  ],
  colorDanger: ['--color-status-danger']
}

const RADIUS_TOKEN: Record<Radius, string> = {
  none: '0',
  sm: 'var(--radius-field)',
  md: 'var(--radius-control)',
  lg: 'var(--radius-container)'
}

const FONT_STACK: Record<FontPreset, string> = {
  system:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  sans: 'Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  brand: typography.fontBody
}

export function isFontPreset(value: unknown): value is FontPreset {
  return (
    value === 'system' ||
    value === 'sans' ||
    value === 'serif' ||
    value === 'brand'
  )
}

const FONT_LIST = /^[A-Za-z0-9 ,\-'"]+$/

export function isFontFamily(value: unknown): value is FontFamily {
  return (
    typeof value === 'string' &&
    value.length <= 200 &&
    FONT_LIST.test(value) &&
    /[A-Za-z0-9]/.test(value)
  )
}

function fontStack(font: FontFamily): string {
  return isFontPreset(font) ? FONT_STACK[font] : font
}

/**
 * Checks that a value is one of the theme radius names.
 * @param value - The untrusted value.
 * @returns True for `none`, `sm`, `md` or `lg`.
 */
export function isRadius(value: unknown): value is Radius {
  return value === 'none' || value === 'sm' || value === 'md' || value === 'lg'
}

function isColorKey(key: string): key is ColorKey {
  return COLOR_KEY_SET.has(key)
}

function channel(hex: string, offset: number): number {
  const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255
  return value <= 0.039_28 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

function luminance(hex: string): number {
  return (
    0.2126 * channel(hex, 1) +
    0.7152 * channel(hex, 3) +
    0.0722 * channel(hex, 5)
  )
}

/**
 * Computes the WCAG 2 contrast ratio of two `#RRGGBB` colors.
 * @param a - The first color.
 * @param b - The second color.
 * @returns The ratio, from 1 to 21.
 */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Validates an untrusted theme against the allowed keys, formats and contrast.
 * @param raw - A theme from the session.
 * @returns The typed theme, or the first reason it is rejected.
 */
export function validateTheme(raw: unknown): ThemeResult {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ok: false, reason: 'theme must be an object' }
  }
  const theme: Theme = {}
  for (const [key, value] of Object.entries(raw)) {
    if (key === 'fontFamily') {
      if (!isFontFamily(value)) {
        return {
          ok: false,
          reason:
            'fontFamily must be a preset or a font name list of at most 200 characters'
        }
      }
      theme.fontFamily = value
      continue
    }
    if (key === 'radius') {
      if (!isRadius(value)) {
        return { ok: false, reason: 'radius must be none, sm, md or lg' }
      }
      theme.radius = value
      continue
    }
    if (!isColorKey(key)) {
      return { ok: false, reason: `unknown key ${key}` }
    }
    if (
      typeof value === 'string' &&
      (value.includes('url(') || value.includes(';'))
    ) {
      return { ok: false, reason: `${key} is not allowed` }
    }
    if (typeof value !== 'string' || !HEX.test(value)) {
      return { ok: false, reason: `${key} must be #RRGGBB` }
    }
    theme[key] = value
  }
  const background = theme.colorBackground
  if (background) {
    for (const key of CONTRAST_PAIRS) {
      const foreground = theme[key]
      if (foreground && contrastRatio(foreground, background) < WCAG_AA_TEXT) {
        return { ok: false, reason: `${key} on colorBackground fails WCAG AA` }
      }
    }
  }
  return { ok: true, theme }
}

/**
 * Layers themes so that a later one wins key by key.
 * @param themes - Themes in precedence order, lowest first.
 * @returns The combined theme; validate it before use.
 */
export function mergeThemes(...themes: Theme[]): Theme {
  return Object.assign({}, ...themes)
}

/**
 * Maps a validated theme onto the design system's CSS custom properties.
 * @param theme - A validated theme.
 * @returns Custom property names and values for a style attribute.
 */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const key of COLOR_KEYS) {
    const value = theme[key]
    if (value) {
      for (const name of CSS_VAR[key]) vars[name] = value
    }
  }
  if (theme.radius) vars['--terminal-radius'] = RADIUS_TOKEN[theme.radius]
  if (theme.fontFamily) {
    vars['--font-body'] = fontStack(theme.fontFamily)
    vars['--font-display'] =
      theme.fontFamily === 'brand'
        ? typography.fontDisplay
        : fontStack(theme.fontFamily)
  }
  return vars
}

/**
 * Builds the VGS Collect field style from a validated theme.
 * The host draws the border, radius and background.
 * @param theme - A validated theme.
 * @returns The `css` option for each VGS field.
 */
export function vgsFieldCss(theme: Theme): Record<string, unknown> {
  // The iframe cannot read CSS variables. Font and colour literals mirror the name field.
  const fontFamily = theme.fontFamily ?? 'brand'
  return {
    ...(fontFamily === 'brand'
      ? {
          '@font-face': {
            'font-family': 'Roboto',
            'font-style': 'normal',
            'font-weight': typography.weightRegular,
            'font-display': 'swap',
            src: `url('${new URL('../assets/fonts/roboto-latin-400-normal.woff2', import.meta.url).href}') format('woff2')`
          }
        }
      : {}),
    'font-family': fontStack(fontFamily),
    'font-size': typography.typeBodySmSize,
    'font-weight': typography.weightRegular,
    'line-height': typography.typeBodyLh,
    'font-variant-numeric': 'tabular-nums',
    color: theme.colorText ?? colors.colorTextHeading,
    'background-color': 'transparent',
    border: '0',
    padding: `0 ${spacing.space4}`,
    height: '100%',
    width: '100%',
    'box-sizing': 'border-box',
    'text-align': 'left',
    '&::placeholder': { color: theme.colorText ?? colors.colorTextSecondary },
    '&:focus': { outline: '0' }
  }
}
